import { CheckCircle2, Download, Lock, Clock } from "lucide-react";
import { API_URL } from "../lib/api";
import { getStoredToken } from "../lib/clientStorage";
import { formatCurrency } from "../contexts/FxContext";
import { DEFAULT_CURRENCY } from "../lib/defaultCurrency";

function formatDate(iso) {
    try { return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }); }
    catch (_e) { return iso; }
}
function shortTxn(t) { return t ? `${t.slice(0, 8)}…${t.slice(-4)}` : "—"; }

const STATUS_BADGE = {
    paid: { icon: CheckCircle2, label: "Paid", className: "bg-[#00BF0D]/10 border-[#00BF0D]/20 text-[#00BF0D]" },
    succeeded: { icon: CheckCircle2, label: "Paid", className: "bg-[#00BF0D]/10 border-[#00BF0D]/20 text-[#00BF0D]" },
    recorded: { icon: CheckCircle2, label: "Recorded", className: "bg-white/[0.04] border-white/10 text-white/60" },
    processing: { icon: Clock, label: "Processing", className: "bg-amber-500/10 border-amber-500/20 text-amber-300" },
    partial: { icon: Clock, label: "Partial", className: "bg-amber-500/10 border-amber-500/20 text-amber-300" },
    failed: { icon: Clock, label: "Failed", className: "bg-rose-500/10 border-rose-500/20 text-rose-300" },
    queued: { icon: Clock, label: "Queued", className: "bg-white/[0.04] border-white/10 text-white/55" },
};

function statusBadge(status) {
    return STATUS_BADGE[status] || STATUS_BADGE.recorded;
}

export function PayoutHistory({ payouts = [], poolId, poolName, currency = DEFAULT_CURRENCY }) {
    const fmt = (amount, rowCurrency) => formatCurrency(amount, rowCurrency || currency, { decimals: 2 });
    const csvHref = poolId ? `${API_URL}/pools/${poolId}/payouts.csv?token=${encodeURIComponent(getStoredToken() || "")}` : null;
    return (
        <section className="surface p-5 sm:p-6" data-testid="payout-history">
            <div className="flex items-end justify-between gap-3 mb-4 flex-wrap">
                <div>
                    <p className="label-eyebrow inline-flex items-center gap-1.5">
                        <Lock className="h-3 w-3 text-[#00BF0D]" /> Immutable ledger
                    </p>
                    <h3 className="font-heading text-xl sm:text-2xl tracking-tight mt-1">Payout history</h3>
                </div>
                <div className="flex items-center gap-2">
                    <p className="text-xs text-white/45 font-mono">{payouts.length} entries</p>
                    {csvHref && (
                        <a
                            href={csvHref}
                            className="btn-ghost rounded-md px-2.5 py-1.5 text-xs inline-flex items-center gap-1.5"
                            data-testid="csv-export-button"
                            download
                        >
                            <Download className="h-3.5 w-3.5" /> Export CSV
                        </a>
                    )}
                </div>
            </div>

            {/* Desktop table */}
            <div className="hidden sm:block overflow-hidden rounded-lg border border-white/[0.05]">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="bg-white/[0.025] text-left text-white/45">
                            <th className="px-4 py-2.5 font-medium label-eyebrow">Date</th>
                            <th className="px-4 py-2.5 font-medium label-eyebrow">Transaction</th>
                            <th className="px-4 py-2.5 font-medium label-eyebrow">Method</th>
                            <th className="px-4 py-2.5 font-medium label-eyebrow">Amount</th>
                            <th className="px-4 py-2.5 font-medium label-eyebrow text-right">Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {payouts.length === 0 && (
                            <tr>
                                <td colSpan={5} className="px-4 py-8 text-center text-white/40 text-sm space-y-1">
                                    <p>No payouts yet.</p>
                                    <p className="text-xs text-white/30 mt-1">Client payments appear above under Money in. Use Run payout to send shares to recipients.</p>
                                </td>
                            </tr>
                        )}
                        {payouts.map((p) => {
                            const badge = statusBadge(p.status);
                            const BadgeIcon = badge.icon;
                            return (
                                <tr key={p.id} className="border-t border-white/[0.04] hover:bg-white/[0.02] transition-colors" data-testid={`payout-row-${p.id}`}>
                                    <td className="px-4 py-3 text-white/85">{formatDate(p.date)}</td>
                                    <td className="px-4 py-3 font-mono text-white/65 text-xs">{shortTxn(p.txn_id)}</td>
                                    <td className="px-4 py-3 text-white/65 text-xs uppercase tracking-wider">{p.method}</td>
                                    <td className="px-4 py-3 font-mono text-[#00BF0D] font-semibold tabular-nums">{fmt(p.total_amount, p.currency)}</td>
                                    <td className="px-4 py-3 text-right">
                                        <span className={`fs-pill gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${badge.className}`}>
                                            <BadgeIcon className="h-3 w-3" /> {badge.label}
                                        </span>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Mobile cards */}
            <div className="sm:hidden space-y-2">
                {payouts.length === 0 && (
                    <div className="text-center text-white/40 py-6 text-sm space-y-1">
                        <p>No payouts yet.</p>
                        <p className="text-xs text-white/30">Client payments are under Money in. Use Run payout to distribute to recipients.</p>
                    </div>
                )}
                {payouts.map((p) => {
                    const badge = statusBadge(p.status);
                    const BadgeIcon = badge.icon;
                    return (
                        <div key={p.id} className="rounded-xl border border-white/[0.05] bg-white/[0.02] p-3.5" data-testid={`payout-card-${p.id}`}>
                            <div className="flex items-center justify-between">
                                <p className="text-sm text-white/75">{formatDate(p.date)}</p>
                                <span className={`fs-pill gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${badge.className}`}>
                                    <BadgeIcon className="h-3 w-3" /> {badge.label}
                                </span>
                            </div>
                            <p className="font-mono text-xl text-[#00BF0D] mt-1 tabular-nums">{fmt(p.total_amount, p.currency)}</p>
                            <p className="text-[11px] text-white/40 mt-1 font-mono">{shortTxn(p.txn_id)} · {p.method}</p>
                        </div>
                    );
                })}
            </div>
            {poolName && (
                <p className="mt-4 text-[11px] text-white/30 inline-flex items-center gap-1">
                    <Lock className="h-3 w-3" /> All entries are append-only and signed in the audit log.
                </p>
            )}
        </section>
    );
}
